import { QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof Error && 'response' in error) {
          const status = (error as { response?: { status?: number } }).response?.status;
          if (status && status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Something went wrong';
        toast.error(message);
      },
    },
  },
});

// Query keys factory for type-safe query keys
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
  },

  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    charts: (period: string) => [...queryKeys.dashboard.all, 'charts', period] as const,
    recentBookings: () => [...queryKeys.dashboard.all, 'recent-bookings'] as const,
  },

  // Customers
  customers: {
    all: ['customers'] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.customers.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.customers.all, 'detail', id] as const,
    bookings: (id: string) => [...queryKeys.customers.all, id, 'bookings'] as const,
    payments: (id: string) => [...queryKeys.customers.all, id, 'payments'] as const,
  },

  // Drivers
  drivers: {
    all: ['drivers'] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.drivers.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.drivers.all, 'detail', id] as const,
    kyc: (id: string) => [...queryKeys.drivers.all, id, 'kyc'] as const,
    earnings: (id: string) => [...queryKeys.drivers.all, id, 'earnings'] as const,
    vehicle: (id: string) => [...queryKeys.drivers.all, id, 'vehicle'] as const,
  },

  // Bookings
  bookings: {
    all: ['bookings'] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.bookings.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.bookings.all, 'detail', id] as const,
    tracking: (id: string) => [...queryKeys.bookings.all, id, 'tracking'] as const,
  },

  // Pricing
  pricing: {
    all: ['pricing'] as const,
    fareRules: (params?: Record<string, unknown>) => [...queryKeys.pricing.all, 'fare-rules', params] as const,
    commission: () => [...queryKeys.pricing.all, 'commission'] as const,
  },

  // Payments
  payments: {
    all: ['payments'] as const,
    list: (params: Record<string, unknown>) => [...queryKeys.payments.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.payments.all, 'detail', id] as const,
    payouts: (params: Record<string, unknown>) => [...queryKeys.payments.all, 'payouts', params] as const,
  },

  // Support
  support: {
    all: ['support'] as const,
    tickets: (params: Record<string, unknown>) => [...queryKeys.support.all, 'tickets', params] as const,
    ticket: (id: string) => [...queryKeys.support.all, 'ticket', id] as const,
  },

  // Reports
  reports: {
    all: ['reports'] as const,
    bookings: (params: Record<string, unknown>) => [...queryKeys.reports.all, 'bookings', params] as const,
    revenue: (params: Record<string, unknown>) => [...queryKeys.reports.all, 'revenue', params] as const,
    drivers: (params: Record<string, unknown>) => [...queryKeys.reports.all, 'drivers', params] as const,
  },

  // Fraud
  fraud: {
    all: ['fraud'] as const,
    reports: (params: Record<string, unknown>) => [...queryKeys.fraud.all, 'reports', params] as const,
    customerReports: (id: string) => [...queryKeys.fraud.all, 'customer-reports', id] as const,
    customerBlocks: (id: string) => [...queryKeys.fraud.all, 'customer-blocks', id] as const,
    unblockRequests: (params: Record<string, unknown>) => [...queryKeys.fraud.all, 'unblock-requests', params] as const,
    intelligenceOverview: () => [...queryKeys.fraud.all, 'intelligence-overview'] as const,
    driverRiskScores: (limit: number) => [...queryKeys.fraud.all, 'driver-risk', limit] as const,
    flaggedBookings: () => [...queryKeys.fraud.all, 'flagged-bookings'] as const,
    driverIncidents: (params: Record<string, unknown>) => [...queryKeys.fraud.all, 'driver-incidents', params] as const,
  },

  // Zipto Shield
  shield: {
    all: ['shield'] as const,
    balance: () => [...queryKeys.shield.all, 'balance'] as const,
    transactions: (params: Record<string, unknown>) => [...queryKeys.shield.all, 'transactions', params] as const,
  },
};
